import { NuqsAdapter } from 'nuqs/adapters/next/pages'
import React, { ReactElement, ReactNode, useEffect, type JSX } from 'react'
import type { NextPage } from 'next'
import type { AppContext, AppProps } from 'next/app'
import App from 'next/app'
import ReactModal from 'react-modal'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PlatformConfig } from '@reward-platform/platform-config'
import { useRouter } from 'next/router'
import { DatadogPagesRouter } from '@datadog/browser-rum-nextjs'
import Layout from '~/components/shared/Layout'
import { DatadogInit } from '~/components/shared/Scripts'
import { LocalisationProvider, Messages } from '~/context/localisation'
import { AuthProvider, type AviosAuthUserSession } from '~/context/auth'
import QueryClientProvider from '~/context/queryClient'
import { PlatformConfigContext } from '~/context/platform'
import { UserProvider } from '~/context/user'
import { ThemeProvider } from '~/context/theme'
import { FeatureFlagProvider } from '~/context/featureFlag'
import { AccountProvider } from '~/context/account'
import { ErrorBoundary } from '~/components/shared/ErrorBoundary'
import useCorrelationId from '~/hooks/useCorrelationId/useCorrelationId'
import useRemoveExpiredBasketItems from '~/hooks/useRemoveExpiredBasketItems'
import { ClearBasketItemsOnPlatformSwitchProvider } from '~/hooks/useClearBasketItemsOnPlatformSwitch'
import { MaintenanceModePage } from '~/components/shared/MaintenanceModePage'
import { PaymentOptionsProvider } from '~/components/orders/PaymentOptionsContext/PaymentOptionsProvider'
import { PurchaseComponentsProvider } from '~/context/purchaseComponents'

// API
import {
  setPartnerHeader,
  setCorrelationIdHeader,
  setPlatformTypeHeader,
  setApiRouteBaseUrl,
} from '~/services/clients/nextApiClient'

// Schemas
import { FeatureFlagMap } from '~/schemas/featureFlags'

import { getNonceFromRequest } from '~/server/utils/nonce'
import { getPlatformConfigFromPageContext } from '~/server/pageHandlers/platformConfig.handler'

import '../styles/global.css'
import '../styles/typography.css'
import '~/styles/chat-widget.css'

import { NotificationProvider } from '~/context/notification'
import { isPaymentPage } from '~/server/utils/middlewareUtils'
import { ApplePayScript } from '~/components/shared/Scripts/ApplePayScript'
import { isAviosAuthEnabled } from '~/utils/isAviosAuthEnabled'

ReactModal.setAppElement('#__next')

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement<React.PropsWithChildren>) => ReactNode
}

type AppServerProps = PlatformConfig & {
  inMaintenanceMode?: boolean
  featureFlags?: FeatureFlagMap
  initialMessages?: Messages
  nonce?: string
  aviosAuthEnabled: boolean
  aviosAuthInitialUser?: AviosAuthUserSession | null
}

interface MyAppProps extends AppServerProps, AppProps {
  Component: NextPageWithLayout
}

const DEFAULT_MESSAGE_CLUSTER = [
  'reward-platform', // legacy message formats
  'reward-platform-microcopy',
  'reward-platform-microcopy-2',
]
const initialClusters =
  process.env.NEXT_PUBLIC_MESSAGE_CLUSTERS?.split(',') ?? DEFAULT_MESSAGE_CLUSTER

const ClearExpiredBasketItems = () => {
  useRemoveExpiredBasketItems()

  return null
}

const useSetApiClient = ({ opCo, theme, basePath }: PlatformConfig) => {
  const { correlationId } = useCorrelationId()

  // test this is even needed, maybe reckon middleware can add this
  useEffect(() => {
    setPartnerHeader(opCo)
  }, [opCo])

  useEffect(() => {
    setApiRouteBaseUrl(basePath)
  }, [basePath])

  useEffect(() => {
    const platformType = theme === 'avios' ? 'avios' : 'whitelabel'
    setPlatformTypeHeader(platformType)
  }, [theme])

  useEffect(() => {
    if (correlationId) {
      setCorrelationIdHeader(correlationId)
    }
  }, [correlationId])
}

// This hook performs a full page reload when the user navigates to or from the payment page.
// This is necessary to ensure that the new CSP rules are applied.
const useFullPageReloadForPaymentPage = () => {
  const router = useRouter()
  useEffect(() => {
    const getPathname = (url: string) => new URL(url, window.location.origin).pathname
    const handleRouteChangeStart = (url: string) => {
      const newPathname = getPathname(url)
      const currentPathname = window.location.pathname

      const goingToPayment = isPaymentPage(newPathname)
      const leavingPayment = isPaymentPage(currentPathname) && !goingToPayment

      if (goingToPayment || leavingPayment) {
        window.location.href = url // force full page reload

        // Signal to Next.js that the route change was cancelled to avoid error reporting
        const cancellationError: Error & { cancelled: boolean } = Object.assign(
          new Error('Route change aborted for full page reload'),
          { cancelled: true }
        )

        router.events.emit('routeChangeError', cancellationError, url, { shallow: false })
      }
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [router.events])
}

const PaymentPageFullPageReload = () => {
  useFullPageReloadForPaymentPage()
  return null
}

function MyApp(props: Readonly<MyAppProps>): JSX.Element {
  const {
    Component,
    pageProps,
    opCo,
    theme: platformTheme,
    locale,
    inMaintenanceMode,
    featureFlags,
    initialMessages = {},
    nonce,
    aviosAuthEnabled,
    aviosAuthInitialUser,
  } = props

  useSetApiClient(props)

  const getLayout = Component.getLayout ?? ((page) => <Layout variant="search">{page}</Layout>)

  if (inMaintenanceMode) {
    return <MaintenanceModePage themeId={platformTheme} />
  }

  return (
    <ErrorBoundary opCo={opCo} themeId={platformTheme}>
      <DatadogPagesRouter />
      <PlatformConfigContext.Provider value={props}>
        <NuqsAdapter>
          <ThemeProvider themeId={platformTheme} nonce={nonce}>
            <LocalisationProvider
              locale={locale}
              initialMessages={pageProps.initialMessages ?? initialMessages}
              initialClusterKeys={initialClusters}
            >
              <NotificationProvider>
                <QueryClientProvider>
                  <AuthProvider
                    platformTheme={platformTheme}
                    aviosAuthEnabled={aviosAuthEnabled}
                    initialUser={aviosAuthInitialUser}
                  >
                    <FeatureFlagProvider initialFeatureFlags={featureFlags}>
                      <PurchaseComponentsProvider>
                        <PaymentPageFullPageReload />
                        <DatadogInit />
                        <ApplePayScript nonce={nonce} />
                        {/* disabled in prod by default */}
                        <ReactQueryDevtools />
                        <UserProvider>
                          <ClearBasketItemsOnPlatformSwitchProvider>
                            <ClearExpiredBasketItems />
                            <AccountProvider>
                              <PaymentOptionsProvider useStorage>
                                {getLayout(<Component {...pageProps} />)}
                              </PaymentOptionsProvider>
                            </AccountProvider>
                          </ClearBasketItemsOnPlatformSwitchProvider>
                        </UserProvider>
                      </PurchaseComponentsProvider>
                    </FeatureFlagProvider>
                  </AuthProvider>
                </QueryClientProvider>
              </NotificationProvider>
            </LocalisationProvider>
          </ThemeProvider>
        </NuqsAdapter>
      </PlatformConfigContext.Provider>
    </ErrorBoundary>
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
MyApp.getInitialProps = async (appContext: AppContext) => {
  const isServerSide = typeof window === 'undefined'

  const appProps = await App.getInitialProps(appContext)
  const platformConfig = getPlatformConfigFromPageContext(appContext.ctx)
  const nonce = getNonceFromRequest(appContext.ctx.req)
  const aviosAuthEnabled = isAviosAuthEnabled(platformConfig.buildType)

  const defaultProps: AppServerProps = {
    ...appProps,
    ...platformConfig,
    nonce,
    aviosAuthEnabled,
  }

  if (!isServerSide) {
    return defaultProps
  }

  const { logger, logRequestToDatadog } = await import('~/server/logger')
  const { NextPageError } = await import('~/server/errors')
  const { getAllFeatureVariablesForUser } = await import('~/server/featureFlags')
  const {
    handleRedirectToAviosPlatform,
    handleRedirectToAviosProductPage,
    handleRedirectToFallbackUrl,
    handleSilentLogin,
  } = await import('~/server/pageHandlers')

  const request = appContext.ctx.req
  const response = appContext.ctx.res

  if (!!response && !!request) {
    try {
      const { buildUnrestrictedRootPaths, isAuthRequiredForPath, isUserAuthenticated } =
        await import('~/server/pageHandlers/pageContext.utils')
      const currentPath = appContext.ctx.asPath ?? appContext.ctx.pathname ?? '/'
      const homePaths = buildUnrestrictedRootPaths(platformConfig.basePath, platformConfig.locale)
      const authRequired = isAuthRequiredForPath(currentPath, homePaths)
      let aviosAuthInitialUser: AviosAuthUserSession | null = null
      if (aviosAuthEnabled) {
        const { getAviosAuthServerSession } = await import(
          '@channel/shared-header-footer/auth-server'
        )
        aviosAuthInitialUser = await getAviosAuthServerSession({
          getHeader: (name: string) => {
            const header = request.headers[name.toLowerCase()]
            if (Array.isArray(header)) {
              return header[0]
            }

            return header
          },
          opco: platformConfig.opCo,
        })
      }
      // When AviosAuth is active, treat auth-required pages as authenticated only after
      // the Avios session has actually resolved.
      let authenticated = true
      if (authRequired) {
        authenticated = aviosAuthEnabled
          ? !!aviosAuthInitialUser
          : await isUserAuthenticated(request, response)
      }
      // Fetch feature toggles for user
      const featureFlagVariableMap = await getAllFeatureVariablesForUser(request, response)
      // Fetch local fallback messages from json
      const defaultMessageJson = (await import(`../i18n/defaultMessages/messages.en.json`)).default
      const defaultLegacyMessageJson = (
        await import(`../i18n/defaultMessages/messages.legacy.json`)
      ).default
      const initialMessages = { ...defaultMessageJson, ...defaultLegacyMessageJson }
      defaultProps.initialMessages = initialMessages

      // if feature toggle for maintenance mode is on, return with prop without running rest of function
      const inMaintenanceMode = featureFlagVariableMap['maintenance-mode']?.value
      if (inMaintenanceMode) {
        return { ...defaultProps, inMaintenanceMode }
      }

      const shouldEvaluateRedirects = !authRequired || authenticated
      if (shouldEvaluateRedirects) {
        // Redirects the whitelabel hotels and car hire search pages to avios.com if toggle on
        const shouldRedirectToAviosProductPage =
          !!featureFlagVariableMap['redirect-ancillaries-to-avios']?.value &&
          platformConfig.theme !== 'avios'
        const didRedirectToAviosProductPage = handleRedirectToAviosProductPage(appContext.ctx, {
          enabled: shouldRedirectToAviosProductPage,
          opCo: platformConfig.opCo,
          locale: platformConfig.locale,
        })
        if (didRedirectToAviosProductPage) {
          return { ...defaultProps }
        }

        // Redirects to avios.com from the whitelabel products if feature toggle on
        const shouldRedirectToAvios =
          !!featureFlagVariableMap['redirect-rewards-to-avios']?.value &&
          platformConfig.theme !== 'avios'
        handleRedirectToAviosPlatform(appContext.ctx, shouldRedirectToAvios)

        const didRedirectToFallback =
          !!featureFlagVariableMap['redirect-switchfly-iberia']?.value &&
          handleRedirectToFallbackUrl(appContext.ctx, ['IB', 'EI'])
        if (didRedirectToFallback) {
          return { ...defaultProps }
        }
      }

      const didRedirectToSilentLogin = await handleSilentLogin(appContext.ctx, {
        aviosAuthEnabled,
      })
      if (didRedirectToSilentLogin) {
        return { ...defaultProps }
      }

      // TODO: improve to include generic error page
      // Handle error response
      if (appContext.ctx.err) {
        const pageError = new NextPageError(appContext.ctx.err)
        await logRequestToDatadog(request, response, pageError)
      }

      return {
        ...defaultProps,
        aviosAuthInitialUser,
        featureFlags: featureFlagVariableMap,
        aviosAuthEnabled,
      }
    } catch (error) {
      // TODO: improve error handling using improved system
      logger.error('Error in App.getInitialProps', error)
      return { ...defaultProps }
    }
  }

  return { ...defaultProps }
}

export default MyApp
